import { forwardRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/utils/cn';
import { Icon } from './Icon';
import { ICONS, type IconName } from '@/constants/icons';
import { filterTriggerHover, filterTriggerFocus } from './filterTriggerStyles';
import { SelectChevron } from './Select';

/**
 * CustomSelect Component
 *
 * A branded dropdown select using Radix UI with support for:
 * - Option descriptions
 * - Option icons
 * - Helper text
 * - Required indicator
 * - Error states
 * - Brand colors (coral/teal)
 */

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: IconName;
  /** Custom color for the icon (CSS color value) */
  iconColor?: string;
  disabled?: boolean;
  /** Small status dot before the label (Tailwind bg class), shown in the menu and the trigger */
  dotClass?: string;
}

export interface CustomSelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  /** ID for form association */
  id?: string;
  /** Smaller label for tight spaces */
  compact?: boolean;
  /** Toolbar-chip trigger: 28px (44px below sm), 12px text, shrinks to its content */
  inline?: boolean;
  /** Accessible name for the trigger when there is no visible label */
  ariaLabel?: string;
}

export const CustomSelect = forwardRef<HTMLButtonElement, CustomSelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select an option',
      label,
      helperText,
      error,
      disabled = false,
      required = false,
      className,
      id,
      compact = false,
      inline = false,
      ariaLabel,
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).slice(2, 9)}`;
    const errorId = error ? `${selectId}-error` : undefined;
    const helperId = helperText ? `${selectId}-helper` : undefined;

    // Find the selected option for display
    const selectedOption = options.find((opt) => opt.value === value);

    return (
      <div className={cn('w-full', className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block text-gray-500',
              compact
                ? 'text-[10px] font-medium uppercase tracking-wide mb-1'
                : 'text-xs font-semibold uppercase tracking-wide mb-2'
            )}
          >
            {label}
            {required && (
              <span className="text-danger ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        <SelectPrimitive.Root
          value={value}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger
            ref={ref}
            id={selectId}
            aria-describedby={
              [errorId, helperId].filter(Boolean).join(' ') || undefined
            }
            aria-label={ariaLabel}
            aria-invalid={!!error}
            aria-required={required}
            className={cn(
              // Base styles
              inline ? 'max-w-full h-7 max-sm:h-[44px] px-2.5' : 'w-full',
              !inline && (compact ? 'px-2.5 h-[34px] lg:h-auto lg:py-2' : 'px-3 py-2.5'),
              'flex items-center justify-between gap-2',
              'bg-white',
              'border rounded-lg',
              // Mobile renders at 16px to prevent iOS Safari zoom-on-focus; desktop unchanged.
              inline ? 'text-xs' : compact ? 'text-[16px] lg:text-[13px]' : 'text-[16px] lg:text-sm',
              'transition-[border-color,background-color,color,opacity] duration-200',
              'cursor-pointer',
              // Default border
              error ? 'border-danger' : 'border-gray-300',
              // Focus state - using primary color from palette
              filterTriggerFocus,
              // Hover state - subtle gray
              !disabled && (inline ? filterTriggerHover : 'hover:border-gray-400 hover:bg-gray-50/50'),
              // Disabled state
              disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
              // Data states for Radix
              'data-[state=open]:border-primary-600'
            )}
          >
            <SelectPrimitive.Value
              placeholder={
                <span className="text-gray-400">{placeholder}</span>
              }
            >
              {/* Custom value display with icon */}
              {selectedOption && (
                <span className="flex min-w-0 items-center gap-2">
                  {selectedOption.dotClass && (
                    <span className={cn('h-2 w-2 flex-shrink-0 rounded-full', selectedOption.dotClass)} aria-hidden="true" />
                  )}
                  {selectedOption.icon && (
                    <Icon
                      name={selectedOption.icon}
                      size="sm"
                      color={selectedOption.iconColor ? undefined : 'text-gray-600'}
                      style={selectedOption.iconColor ? { color: selectedOption.iconColor } : undefined}
                    />
                  )}
                  <span className="truncate text-gray-900" title={selectedOption.label}>{selectedOption.label}</span>
                </span>
              )}
            </SelectPrimitive.Value>

            <SelectPrimitive.Icon className="flex-shrink-0">
              <SelectChevron className="text-gray-500" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className={cn(
                'overflow-hidden bg-white rounded-lg',
                'border border-gray-200',
                'shadow-lg',
                'z-[100000]',
                // Ensure dropdown matches trigger width
                inline
                  ? 'min-w-[max(var(--radix-select-trigger-width),220px)] max-w-[min(320px,calc(100vw-24px))]'
                  : 'min-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)]',
                'max-h-[300px]',
                // Animation
                'animate-in fade-in-0 zoom-in-95',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                'data-[side=bottom]:slide-in-from-top-2',
                'data-[side=top]:slide-in-from-bottom-2'
              )}
              position="popper"
              sideOffset={4}
              align="start"
            >
              <SelectPrimitive.ScrollUpButton className="flex items-center justify-center h-6 bg-white cursor-default">
                <Icon name={ICONS.ANGLE_UP} size="sm" color="text-gray-500" />
              </SelectPrimitive.ScrollUpButton>

              <SelectPrimitive.Viewport className="p-1">
                {options.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={cn(
                      'relative flex items-center w-full',
                      inline ? 'px-2.5 py-2 max-sm:min-h-[44px] rounded-md' : compact ? 'px-2.5 py-1.5 lg:py-2 rounded-md' : 'px-3 py-2.5 rounded-md',
                      compact ? 'text-xs lg:text-[13px] text-gray-700' : 'text-sm text-gray-700',
                      'cursor-pointer select-none',
                      'transition-colors duration-150',
                      // Focus/hover state
                      'focus:bg-gray-100 focus:outline-none',
                      'hover:bg-gray-100',
                      // Selected state styling
                      'data-[state=checked]:bg-primary-50 data-[state=checked]:text-primary-700',
                      // Disabled state
                      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed data-[disabled]:hover:bg-transparent'
                    )}
                  >
                    {/* Content container with proper alignment */}
                    <div className="flex items-center gap-2 w-full">
                      {/* Checkmark space - always reserve space for alignment */}
                      <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                        <SelectPrimitive.ItemIndicator>
                          <Icon
                            name={ICONS.CHECK}
                            size="xs"
                            color="text-primary-500"
                          />
                        </SelectPrimitive.ItemIndicator>
                      </span>

                      {option.dotClass && (
                        <span className={cn('h-2 w-2 flex-shrink-0 rounded-full', option.dotClass)} aria-hidden="true" />
                      )}

                      {/* Icon if provided */}
                      {option.icon && (
                        <Icon
                          name={option.icon}
                          size="sm"
                          color={option.iconColor ? undefined : 'text-gray-600'}
                          className="flex-shrink-0"
                          style={option.iconColor ? { color: option.iconColor } : undefined}
                        />
                      )}

                      {/* Label and description */}
                      <div className="flex-1 min-w-0">
                        <SelectPrimitive.ItemText>
                          <span className="font-medium text-gray-900 block truncate" title={option.label}>
                            {option.label}
                          </span>
                        </SelectPrimitive.ItemText>

                        {option.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {option.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>

              <SelectPrimitive.ScrollDownButton className="flex items-center justify-center h-6 bg-white cursor-default">
                <Icon name={ICONS.ANGLE_DOWN} size="sm" color="text-gray-500" />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {/* Helper text */}
        {helperText && !error && (
          <p
            id={helperId}
            className="mt-1.5 text-xs text-gray-500"
          >
            {helperText}
          </p>
        )}

        {/* Error message */}
        {error && (
          <p
            id={errorId}
            className="mt-1.5 text-xs text-danger flex items-center gap-1"
            role="alert"
          >
            <Icon name={ICONS.CAUTION} size="xs" />
            {error}
          </p>
        )}
      </div>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

/**
 * Convenience type exports
 */
export type { IconName } from '@/constants/icons';
